import { Tier } from 'app/shared/account/tier.model';
import { ExplainerContent } from '../shared/explainer-dialog/explainer-dialog-content';

export interface IntegrationSettings {
  type?: IntegrationType;
  isAuthenticated?: boolean;
  data?: any;
  // This can be added from front-end based on the enum on app-property
  loadingDetails?: boolean;
  errorKey?: string;
  config?: IntegrationConfig;
  isDisable?: boolean;
  /** True when the integration was created by an app-store app (e.g. the Shopware app) — manual edit/delete in Client then needs a warning. */
  installedViaAppStore?: boolean;
}

export interface IntegrationConfig {
  type?: IntegrationType;
  logoUrl?: string;
  descriptionRows?: Array<IntegrationDetailsRow>;
  authenticationForm?: Array<IntegrationInputConfig>;
  configurationForm?: Array<Array<IntegrationInputConfig>>;
  restApiInformation?: RestApiInformation;
  instructionsUrl?: string;
  /** Optional translation key shown as a top-of-body description inside the Configure dialog (supports HTML for links). */
  configurationDescriptionKey?: string;
  paidAddon?: boolean;
  adminOnly?: boolean;
  isBeta?: boolean;
  isNew?: boolean;
  disabledOnTiers?: Tier[];
  useOAuth?: boolean;
  /**
   * For OAuth integrations whose client_id / client_secret / endpoints are configured
   * per-tenant: the UI must persist the authenticationForm values BEFORE opening
   * the OAuth authorize popup, so the backend can read the saved settings when
   * building the authorize URL.
   */
  oauthRequiresSettings?: boolean;
}

export interface RestApiInformation {
  textKey?: string;
  inviteUrl?: string;
  textParams?: Record<string, string>;
}

export interface IntegrationDetailsRow {
  textKey?: string;
  bulletPoint?: string;
  imageUrl?: string;
  paidAddon?: string;
}

export interface IntegrationInputConfig {
  type?: IntegrationInputType;
  subtype?: IntegrationInputSubType;
  key?: string;
  required?: boolean;
  /** Only render (and validate) this field when another form value matches one of the given values. */
  visibleWhen?: { key: string; values: Array<string> };
  /** When true, validateStep requires the value to start with http:// or https:// if non-empty. */
  isUrl?: boolean;
  /** NewLineToCsv only: the backend keys a dictionary on these lines, so validateStep rejects repeats and save drops blank lines. */
  uniqueLineKey?: IntegrationLineKeyPart;
  labelKey?: string;
  descriptionKey?: string;
  placeholderKey?: string;
  defaultValue?: string;
  explainerContent?: ExplainerContent;
  instruction?: string;
  propertyConversion?: IntegrationPropertyConversion;
  errorKey?: string;
  errorParams?: Record<string, string>;
  options?: Array<IntegrationDropdownOptions>;
  optionsUrl?: string;
  optionsLoading?: boolean;
  optionsCheckAll?: boolean;
  optionsWaitingInput?: boolean;
  optionsRefreshAfterChoice?: Array<string>;
  optionsUseValuesFromChoice?: Array<string>;
  optionsIdPropertyName?: string;
  optionsValuePropertyName?: string;
  optionsCustomParsing?: IntegrationOptionsParsingType;
  urlStripRegex?: string;
  validationError?: boolean;
}

export enum IntegrationOptionsParsingType {
  ApsisOneSections = 'ApsisOneSections',
  ApsisOneKeyspaces = 'ApsisOneKeyspaces',
  ApsisOneEmailFieldIds = 'ApsisOneEmailFieldIds',
}

export enum IntegrationPropertyConversion {
  NewLineToCsv = 'NewLineToCsv',
}

export enum IntegrationLineKeyPart {
  WholeLine = 'WholeLine',
  AfterLastEquals = 'AfterLastEquals',
}

export interface IntegrationDropdownOptions {
  key?: IntegrationDropdownOptionValues | string;
  translationKey?: string;
}

export enum IntegrationDropdownOptionValues {
  // Voyado Properties to update
  FirstSeen = 'first_seen',
  Goals = 'Goals',
  ProflN1 = 'ProflN1',
  ProflV1 = 'ProflV1',
  ProflN2 = 'ProflN2',
  ProflV2 = 'ProflV2',
  ProflN3 = 'ProflN3',
  ProflV3 = 'ProflV3',
  LastConsentCheckboxPresent = 'last_consent_checkbox_present',
  LastConsentContextDomain = 'last_consent_context_domain',
  LastConsentContextTitle = 'last_consent_context_title',
  LastConsentDate = 'last_consent_date',
  LastConsentPolicyRevision = 'last_consent_policy_revision',
  LastConsentPolicyVersion = 'last_consent_policy_version',
  LastSeen = 'last_seen',
  /*Name = 'Name',
  Optin = 'Optin',
  OptinData = 'OptinData',
  Org = 'Org',
  Rating = 'Rating',
  RatingM = 'RatingM',*/

  LastSrc = 'LastSrc',
  SessionActivity = 'SessionActivity',
  AffBrnd = 'AffBrnd',
  AffSize = 'AffSize',
  AffColr = 'AffColr',
  AffCatg = 'AffCatg',
  AffUrl = 'AffUrl',
  AffGndr = 'AffGndr',

  // Voyado subscriber check
  AcceptsEmail = 'AcceptsEmail',
  AcceptsSms = 'AcceptsSms',
  AcceptsPostal = 'AcceptsPostal',

  // Voyado contact types
  Contact = 'Contact',
  Member = 'Member',
}

export enum IntegrationInputType {
  InputField = 'InputField',
  TextArea = 'TextArea',
  SingleChoiceDropdown = 'SingleChoiceDropdown',
  MultiChoiceDropdown = 'MultiChoiceDropdown',
  ToggleButton = 'ToggleButton',
  SegmentPicker = 'SegmentPicker',
  /**
   * Renders a list of repeatable rows, each with a dropdown (source field, picked from
   * `options`) and a free-text destination-path input. The bound value is
   * `Array<{ sourceField: string; destinationPath: string }>`.
   */
  MultiFieldMapping = 'MultiFieldMapping',
}

export interface FieldMapping {
  sourceField?: string;
  destinationPath?: string;
}

export enum IntegrationInputSubType {
  Text = 'text',
  Number = 'number',
  Password = 'password',
}

/** Mirrors CustomAuthenticationType in Triggerbee.Integrations.Core. */
export enum CustomAuthenticationType {
  OAuth = 'OAuth',
  ApiKey = 'ApiKey',
  None = 'None',
}

export enum IntegrationType {
  Apsis = 'Apsis',
  ApsisOne = 'ApsisOne',
  Brevo = 'Brevo',
  Custobar = 'Custobar',
  Dotdigital = 'Dotdigital',
  Emarsys = 'Emarsys',
  Ftp = 'Ftp',
  SiftLab = 'SiftLab',
  Klaviyo = 'Klaviyo',
  MailChimp = 'MailChimp',
  MediaConnect = 'MediaConnect',
  Omnisend = 'Omnisend',
  Pipedrive = 'Pipedrive',
  RuleMailer = 'RuleMailer', // Obsolete, should be removed when possible
  Rule = 'Rule',
  ScrollLogging = 'ScrollLogging',
  Trello = 'Trello',
  Voyado = 'Voyado',
  Zapier = 'Zapier',
  MailerLite = 'MailerLite',
  MailerSend = 'MailerSend',
  Centra = 'Centra',
  Shopify = 'Shopify',
  CleverReach = 'CleverReach',
  Shopware = 'Shopware',
  Custom = 'Custom',
}

export interface IntegrationListKeyValuePair {
  id?: string;
  name?: string;
}

export interface ProductRecommendationsIntegration {
  type?: IntegrationType;
  dataSets?: Array<ProductRecommendationsIntegrationDataset>;
}

export interface ProductRecommendationsIntegrationDataset {
  name?: string;
  id?: number;
}
